import { customSymbol, isCustomKey, registerCustomSymbols, type CustomSymbol } from './custom';
import { bbWorld, SHEET, snapV } from './geometry';

const GRID = SHEET.GRID;
import { LIB } from './library';
import { normalisePage } from './project';
import { newId, nextTag } from './tagging';
import type { Page, Project, Pt, Sym, SymProps, Wire } from './types';
import { t as $t } from './i18n';


/*
 * Window macros: a reusable piece of schematic (symbols + wires) stored with
 * coordinates relative to its top-left grid point. Inserting a macro gives its
 * devices fresh tags while keeping the relations inside it (a coil and its
 * contacts stay one device).
 */

export interface Macro {
  name: string;
  desc: string;
  syms: Sym[];
  wires: Wire[];
  /** page macro: whole pages instead of a window (syms / wires are then empty) */
  pages?: MacroPage[];
  /** value sets (variants) for the {{PLACEHOLDER}} texts in the macro */
  sets?: ValueSet[];
  /** user symbols the macro uses */
  symbols?: CustomSymbol[];
}

/** One page of a page macro. */
export type MacroPage = Omit<Page, 'id' | 'rev'>;

/** A named variant: a value for every placeholder, e.g. "4 kW" → {GÜÇ: '4 kW', ŞALTER: 'MKS-10'}. */
export interface ValueSet {
  name: string;
  values: Record<string, string>;
}

export interface MacroSummary {
  id: string;
  name: string;
  desc: string;
  builtin: boolean;
  data: Macro;
}

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v));

/** Creates a macro from symbols and wires, moving them so the top-left sits on (0, 0). */
export function makeMacro(name: string, desc: string, syms: Sym[], wires: Wire[]): Macro {
  const xs = [...syms.map((s) => bbWorld(s)[0]), ...wires.flatMap((w) => w.pts.map((p) => p[0]))];
  const ys = [...syms.map((s) => bbWorld(s)[1]), ...wires.flatMap((w) => w.pts.map((p) => p[1]))];
  const ox = Math.floor(Math.min(...xs) / GRID) * GRID, oy = Math.floor(Math.min(...ys) / GRID) * GRID;
  const symbols = [...new Set(syms.map((s) => s.type).filter(isCustomKey))].map((k) => customSymbol(k)).filter((c): c is CustomSymbol => !!c);
  return {
    name, desc,
    syms: syms.map((s) => ({ ...clone(s), x: s.x - ox, y: s.y - oy })),
    wires: wires.map((w) => ({ ...clone(w), pts: w.pts.map((p): Pt => [p[0] - ox, p[1] - oy]) })),
    ...(symbols.length ? { symbols } : {}),
  };
}

const prefixOf = (tag: string) => /^[A-Za-z]+/.exec(tag)?.[0] ?? '';

/** Numbers inserted terminals in drawing order, after the highest number their strip already has. */
function numberTerminals(pr: Project, inserted: Sym[]) {
  const ids = new Set(inserted.map((s) => s.id)), next = new Map<string, number>();
  for (const s of inserted) {
    if (LIB[s.type]?.role !== 'term') continue;
    if (!next.has(s.tag)) {
      let m = 0;
      for (const p of pr.pages)
        for (const o of p.syms)
          if (o.type === 'term' && o.tag === s.tag && !ids.has(o.id)) m = Math.max(m, parseInt(o.props.pinNo || '', 10) || 0);
      next.set(s.tag, m);
    }
    const n = next.get(s.tag)! + 1;
    next.set(s.tag, n);
    s.props = { ...s.props, pinNo: String(n) };
  }
}

/* ---------------- placeholders and value sets ---------------- */

const PH = /\{\{\s*([^{}]{1,32}?)\s*\}\}/g;

/** Placeholder names used in a macro's texts, in order of appearance. */
export function macroPlaceholders(m: Pick<Macro, 'syms' | 'wires' | 'pages'>): string[] {
  const out = new Set<string>();
  const json = JSON.stringify([m.syms, m.wires, m.pages ?? []]);
  for (const x of json.matchAll(PH)) out.add(x[1]);
  return [...out];
}

/** Replaces {{NAME}} in every string of a value (deep copy); unknown names stay visible. */
export function fillPlaceholders<T>(v: T, values: Record<string, string>): T {
  const walk = (x: unknown): unknown => {
    if (typeof x === 'string') return x.replace(PH, (all, k: string) => (k in values ? values[k] : all));
    if (Array.isArray(x)) return x.map(walk);
    if (x && typeof x === 'object') return Object.fromEntries(Object.entries(x).map(([k, y]) => [k, walk(y)]));
    return x;
  };
  return walk(v) as T;
}

/** Fresh tags for the devices of a macro (all its pages together), next to what the project has. */
function tagMapFor(pr: Project, syms: Sym[]): Map<string, string> {
  const tagMap = new Map<string, string>();
  const taken = new Set<string>();
  const fresh = (prefix: string) => {
    let t = nextTag(pr, prefix);
    while (taken.has(t)) t = prefix + (parseInt(t.slice(prefix.length), 10) + 1);
    taken.add(t);
    return t;
  };
  for (const s of syms) {
    const role = LIB[s.type]?.role;
    if (!role || !s.tag || tagMap.has(s.tag) || role === 'pot' || role === 'brk' || role === 'text') continue;
    const prefix = prefixOf(s.tag) || LIB[s.type].prefix;
    if (prefix) tagMap.set(s.tag, fresh(prefix));
  }
  return tagMap;
}

/**
 * Copies a macro onto a page at `at`, returning the new objects (already added
 * to the page). Devices, cables and terminal strips get the next free tags.
 */
export function insertMacro(pr: Project, page: Page, m0: Macro, at: Pt, values: Record<string, string> = {}): { syms: Sym[]; wires: Wire[] } {
  const m = fillPlaceholders(m0, values);
  if (m.symbols?.length) registerCustomSymbols(m.symbols);
  const dx = snapV(at[0]), dy = snapV(at[1]);
  const syms: Sym[] = [];
  // pass 1: decide the new tag for every device in the macro (in drawing order)
  const ordered = [...m.syms].filter((s) => LIB[s.type]).sort((a, b) => a.x - b.x || a.y - b.y);
  const tagMap = tagMapFor(pr, ordered);
  for (const s0 of ordered) {
    const s: Sym = { ...clone(s0), id: newId(), x: s0.x + dx, y: s0.y + dy };
    if (tagMap.has(s.tag)) s.tag = tagMap.get(s.tag)!;
    page.syms.push(s);
    syms.push(s);
  }
  // terminals: renumber within the (new) strip in drawing order
  numberTerminals(pr, syms);
  const wires = m.wires.map((w0) => ({ ...clone(w0), id: newId(), pts: w0.pts.map((p): Pt => [p[0] + dx, p[1] + dy]) }));
  for (const w of wires) {
    delete w.no; // frozen wire numbers do not travel with a macro
    page.wires.push(w);
  }
  return { syms, wires };
}

/* ---------------- page macros ---------------- */

/** A page macro from whole pages (kept as they are; ids are renewed on insertion). */
export function makePageMacro(name: string, desc: string, pages: Page[], sets: ValueSet[] = []): Macro {
  const types = new Set(pages.flatMap((p) => p.syms.map((s) => s.type)));
  const symbols = [...types].filter(isCustomKey).map((k) => customSymbol(k)).filter((c): c is CustomSymbol => !!c);
  return {
    name, desc, syms: [], wires: [],
    pages: pages.map(({ id: _id, rev: _rev, ...p }) => clone(p)),
    ...(sets.length ? { sets: clone(sets) } : {}),
    ...(symbols.length ? { symbols } : {}),
  };
}

/**
 * Inserts the pages of a page macro after page index `after` (-1 = at the
 * start). Devices get fresh tags across all inserted pages, so relations
 * between the pages (coil and contacts, cross-references) are kept.
 * Returns the indexes of the new pages.
 */
export function insertPageMacro(pr: Project, m0: Macro, after: number, values: Record<string, string> = {}): number[] {
  const m = fillPlaceholders(m0, values);
  if (m.symbols?.length) registerCustomSymbols(m.symbols);
  const pages = (m.pages ?? []).map((p) => normalisePage(clone(p)));
  const all = pages.flatMap((p) => [...p.syms].sort((a, b) => a.x - b.x || a.y - b.y));
  const tagMap = tagMapFor(pr, all);
  const keyTag = (key: string) => {
    const i = key.lastIndexOf('-');
    const t = key.slice(i + 1);
    return i >= 0 && tagMap.has(t) ? key.slice(0, i + 1) + tagMap.get(t) : key;
  };
  for (const p of pages) {
    p.id = newId();
    for (const s of p.syms) {
      s.id = newId();
      if (tagMap.has(s.tag)) s.tag = tagMap.get(s.tag)!;
    }
    for (const w of p.wires) {
      w.id = newId();
      delete w.no;
    }
    if (p.layout) for (const it of p.layout.items) {
      it.id = newId();
      it.dev = keyTag(it.dev);
    }
  }
  const at = Math.max(0, Math.min(pr.pages.length, after + 1));
  pr.pages.splice(at, 0, ...pages);
  // terminals: continue each strip's numbering after what the project already has
  numberTerminals(pr, pages.flatMap((p) => p.syms));
  return pages.map((_, i) => at + i);
}

/* ---------------- built-in macros ---------------- */

function builder() {
  const syms: Sym[] = [], wires: Wire[] = [];
  const S = (type: string, x: number, y: number, tag = '', desc = '', props: SymProps = {}, part = '') => {
    syms.push({ id: newId(), type, x, y, rot: 0, tag, desc, part, maker: part ? $t("Örnek Katalog") : '', props });
  };
  const W = (pts: Pt[], cs = '', col = '') => wires.push({ id: newId(), pts, cs, col });
  return { syms, wires, S, W };
}

function dolPower(): Macro {
  const { syms, wires, S, W } = builder();
  [0, 8, 16].forEach((x) => W([[x, 0], [x, 8]], '2.5', 'BK'));
  S('cb3', 0, 8, 'Q1', $t("Motor koruma"), {}, 'MKS-4.0');
  S('k3', 0, 44, 'K1');
  [0, 8, 16].forEach((x) => W([[x, 32], [x, 44]], '2.5', 'BK'));
  S('ol3', 0, 76, 'F1', $t("Aşırı yük"), {}, 'TAR-2.5-4');
  [0, 8, 16].forEach((x) => W([[x, 64], [x, 76]], '2.5', 'BK'));
  ['1', '2', '3', '4'].forEach((n, i) => S('term', i * 8, 108, 'X1', '', { pinNo: n }, i === 3 ? 'KL-4-PE' : 'KL-2.5'));
  [0, 8, 16].forEach((x) => W([[x, 96], [x, 108]], '2.5', 'BK'));
  W([[40, 0], [40, 102], [24, 102], [24, 108]], '2.5', 'GNYE');
  S('motor3', 0, 132, 'M1', 'Motor');
  [0, 8, 16, 24].forEach((x) => W([[x, 116], [x, 132]], '2.5', x === 24 ? 'GNYE' : 'BK'));
  S('cable', -8, 124, 'W1', '', { len: 40, cableType: 'NYY-J', cores: '1,2,3,PE', cs: '2.5' });
  return {
    name: $t("Direkt yol verme · güç devresi"),
    desc: $t("Motor koruma şalteri, kontaktör ana kontakları, termik röle, klemensler, motor kablosu ve motor. Üstteki üç tel uçlarını L1-L2-L3 baralarına, sağdaki PE telini PE barasına bağlayın. Bobin için \"Direkt yol verme · kumanda\" makrosunu kullanın."),
    syms, wires,
  };
}

function dolControl(): Macro {
  const { syms, wires, S, W } = builder();
  W([[0, 0], [0, 8]]);
  S('pb_nc', 0, 8, 'S1', 'Stop', {}, 'BT-KR-1NC');
  W([[0, 24], [0, 36]]);
  S('pb_no', 0, 36, 'S2', 'Start', {}, 'BT-YS-1NO');
  S('no', 32, 36, 'K1');
  W([[0, 30], [32, 30], [32, 36]]);
  W([[32, 52], [32, 60], [0, 60]]);
  W([[0, 52], [0, 72]]);
  S('coil', 0, 72, 'K1', $t("Motor kontaktörü"), {}, 'KT-09-230AC');
  W([[0, 92], [0, 100]]);
  return {
    name: $t("Direkt yol verme · kumanda"),
    desc: $t("Stop ve start butonları, mühürleme kontağı ve kontaktör bobini. Üst ucu faz rayına, alt ucu nötr rayına bağlayın. Güç devresindeki kontaktör etiketini bobinle aynı yapın."),
    syms, wires,
  };
}

function starDeltaPower(): Macro {
  const { syms, wires, S, W } = builder();
  [0, 8, 16].forEach((x) => W([[x, 0], [x, 8]], '2.5', 'BK'));
  S('cb3', 0, 8, 'Q1', $t("Motor koruma"), {}, 'MKS-10');
  S('k3', 0, 44, 'K1');
  [0, 8, 16].forEach((x) => W([[x, 32], [x, 44]], '2.5', 'BK'));
  W([[0, 38], [48, 38], [48, 44]], '2.5', 'BK');
  W([[8, 36], [56, 36], [56, 44]], '2.5', 'BK');
  W([[16, 34], [64, 34], [64, 44]], '2.5', 'BK');
  S('k3', 48, 44, 'K3');
  S('ol3', 0, 76, 'F1', $t("Aşırı yük"), {}, 'TAR-7-10');
  [0, 8, 16].forEach((x) => W([[x, 64], [x, 76]], '2.5', 'BK'));
  [48, 56, 64].forEach((x) => W([[x, 64], [x, 108]], '2.5', 'BK'));
  S('k3', 88, 76, 'K2');
  W([[48, 70], [88, 70], [88, 76]], '2.5', 'BK');
  W([[56, 72], [96, 72], [96, 76]], '2.5', 'BK');
  W([[64, 74], [104, 74], [104, 76]], '2.5', 'BK');
  W([[88, 96], [104, 96]], '2.5', 'BK');
  [0, 8, 16, 48, 56, 64, 72].forEach((x, i) => S('term', x, 108, 'X1', '', { pinNo: String(i + 1) }, i === 6 ? 'KL-4-PE' : 'KL-2.5'));
  [0, 8, 16].forEach((x) => W([[x, 96], [x, 108]], '2.5', 'BK'));
  W([[120, 0], [120, 102], [72, 102], [72, 108]], '2.5', 'GNYE');
  S('motor6', 0, 132, 'M1', $t("Motor Y/Δ"));
  [0, 8, 16, 48, 56, 64, 72].forEach((x) => W([[x, 116], [x, 132]], '2.5', x === 72 ? 'GNYE' : 'BK'));
  S('cable', -8, 124, 'W1', '', { len: 88, cableType: 'NYY-J', cores: '1,2,3,4,5,6,PE', cs: '2.5' });
  return {
    name: $t("Yıldız-üçgen yol verme · güç devresi"),
    desc: $t("Şebeke (K1), yıldız (K2) ve üçgen (K3) kontaktörleri, termik röle, 7 uçlu klemens, kablo ve 6 uçlu motor. Üstteki üç tel uçlarını L1-L2-L3 baralarına, sağdaki PE telini PE barasına bağlayın. Bobinler ve zaman rölesi kumanda devresinde çizilir."),
    syms, wires,
  };
}

let builtins: MacroSummary[] | null = null;
export function builtinMacros(): MacroSummary[] {
  builtins ??= [
    ['b:dol-power', dolPower()],
    ['b:dol-control', dolControl()],
    ['b:star-delta-power', starDeltaPower()],
  ].map(([id, m]) => ({ id: id as string, name: (m as Macro).name, desc: (m as Macro).desc, builtin: true, data: m as Macro }));
  return builtins;
}

/** Validates macro JSON from users/files; reuses project validation rules. */
export function normaliseMacro(input: unknown): Macro {
  const d = (input ?? {}) as Record<string, any>;
  if (Array.isArray(d.symbols)) registerCustomSymbols(d.symbols);
  const page = normalisePage({ syms: d.syms, wires: d.wires });
  const pages: MacroPage[] = (Array.isArray(d.pages) ? d.pages.slice(0, 50) : []).map((p: unknown) => {
    const { id: _id, rev: _rev, ...rest } = normalisePage(p);
    return rest;
  });
  if (!page.syms.length && !page.wires.length && !pages.length) throw new Error($t("Makro boş."));
  const allSyms = [...page.syms, ...pages.flatMap((p) => p.syms)];
  const symbols = [...new Set(allSyms.map((s) => s.type).filter(isCustomKey))].map((k) => customSymbol(k)).filter((c): c is CustomSymbol => !!c);
  const sets: ValueSet[] = (Array.isArray(d.sets) ? d.sets.slice(0, 100) : [])
    .map((v: any) => ({
      name: String(v?.name ?? '').trim().slice(0, 60),
      values: Object.fromEntries(Object.entries(v?.values && typeof v.values === 'object' ? v.values : {}).slice(0, 50).map(([k, x]) => [String(k).slice(0, 32), String(x ?? '').slice(0, 200)])),
    }))
    .filter((v: ValueSet) => v.name);
  return {
    name: String(d.name ?? '').slice(0, 80).trim() || $t("Adsız makro"),
    desc: String(d.desc ?? '').slice(0, 500),
    syms: pages.length ? [] : page.syms,
    wires: pages.length ? [] : page.wires,
    ...(pages.length ? { pages } : {}),
    ...(sets.length ? { sets } : {}),
    ...(symbols.length ? { symbols } : {}),
  };
}
